/**
 * 基于 MediaRecorder + Canvas 的纯前端视频压缩
 *----------------------------------------------------
 * 面向 ffmpeg.wasm 无法工作的环境（企业微信 / 微信 WebView / 老 Android WebView）设计。
 * 完全依赖浏览器原生 API，无外部依赖、无大体积 wasm 下载，秒级启动。
 *
 * 压缩原理：
 *   1) <video> 播放原视频，静音、preload='auto'
 *   2) 每一帧通过 requestVideoFrameCallback / requestAnimationFrame 绘制到降分辨率 <canvas>
 *   3) canvas.captureStream() 得到低分辨率视频流
 *   4) 从原 <video>.captureStream() 提取音频 track 合入
 *   5) MediaRecorder 录制成 Blob，比原视频体积小 40-70%
 *
 * 兼容性：
 *   - 企微/微信 Android X5：MP4 通常不支持录制，会自动退化到 WebM（VP8）
 *   - iOS Safari 14.4+：仅支持录制 mp4，需用 video/mp4;codecs="avc1"
 *   - 桌面 Chrome/Edge/Firefox：全支持
 *
 * 已知限制：
 *   - 处理时长 ≈ 原视频播放时长（Canvas 逐帧走播放时钟），无法像 ffmpeg 那样离线加速
 *   - 部分设备上音频轨获取失败，此时会输出无声视频（有兜底提示）
 *   - iOS 部分版本 canvas.captureStream 帧率不稳
 */

/**
 * 默认目标最长边（px）。1080p 视频会被降到 720p，保持画面清晰但体积显著下降。
 * ratio 越接近 1 越大，越接近 0 越小。
 */
const DEFAULT_MAX_DIMENSION = 1280;

/**
 * ratio (0~1] → 目标视频码率 (bps)
 * 300kbps(极限压缩) ~ 4000kbps(接近原画) 线性映射
 * @param {number} ratio
 */
function ratioToVideoBitrate(ratio) {
  const r = Math.max(0, Math.min(1, Number(ratio) || 0));
  return Math.round((300 + 3700 * r) * 1000);
}

/**
 * ratio (0~1] → 目标最长边像素数
 * ratio=1 → DEFAULT_MAX_DIMENSION（1280），ratio→0 → 480
 */
function ratioToMaxDimension(ratio, base = DEFAULT_MAX_DIMENSION) {
  const r = Math.max(0, Math.min(1, Number(ratio) || 0));
  return Math.round(480 + (base - 480) * r);
}

/**
 * 探测 MediaRecorder 支持的最佳 mimeType
 * 优先 mp4/H264（iOS 友好），退化到 webm/vp8（安卓 X5 友好）
 * @returns {{mime: string, ext: string} | null}
 */
export function pickBestRecorderMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = [
    { mime: 'video/mp4;codecs="avc1.42E01E,mp4a.40.2"', ext: 'mp4' }, // iOS 14.4+
    { mime: 'video/mp4;codecs=avc1', ext: 'mp4' },
    { mime: 'video/mp4', ext: 'mp4' },
    { mime: 'video/webm;codecs=vp9,opus', ext: 'webm' },
    { mime: 'video/webm;codecs=vp8,opus', ext: 'webm' },
    { mime: 'video/webm;codecs=vp8', ext: 'webm' },
    { mime: 'video/webm', ext: 'webm' },
  ];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c.mime)) {
        return c;
      }
    } catch (_) { /* ignore */ }
  }
  return null;
}

/**
 * 环境探测：MediaRecorder 压缩管线是否可用
 * @returns {boolean}
 */
export function canRunMediaRecorderCompress() {
  if (typeof document === 'undefined') return false;
  if (typeof MediaRecorder === 'undefined') return false;
  // 需要 canvas.captureStream
  try {
    const c = document.createElement('canvas');
    if (typeof c.captureStream !== 'function') return false;
  } catch (_) { return false; }
  return !!pickBestRecorderMime();
}

/**
 * 保留原文件基名，追加 .compressed.<ext>
 * @param {string} name
 * @param {string} ext
 */
function pickOutputName(name, ext) {
  if (!name || typeof name !== 'string') return `compressed.${ext}`;
  const idx = name.lastIndexOf('.');
  const base = idx > 0 ? name.slice(0, idx) : name;
  return `${base}.compressed.${ext}`;
}

/**
 * 加载 <video> 元数据并可播放（就绪到 HAVE_ENOUGH_DATA）
 * @param {HTMLVideoElement} v
 * @returns {Promise<void>}
 */
function waitCanPlayThrough(v) {
  return new Promise((resolve, reject) => {
    if (v.readyState >= 4) return resolve();
    const onOk = () => { cleanup(); resolve(); };
    const onErr = () => { cleanup(); reject(new Error('视频加载失败')); };
    const cleanup = () => {
      v.removeEventListener('canplaythrough', onOk);
      v.removeEventListener('loadeddata', onOk);
      v.removeEventListener('error', onErr);
    };
    v.addEventListener('canplaythrough', onOk, { once: true });
    // loadeddata 有些环境（iOS）不触发 canplaythrough，用 loadeddata 兜底
    v.addEventListener('loadeddata', onOk, { once: true });
    v.addEventListener('error', onErr, { once: true });
  });
}

/**
 * 主函数：用 MediaRecorder + Canvas 完成视频压缩
 *
 * @param {File|Blob} file
 * @param {Object} [options]
 * @param {number} [options.ratio=0.6]              压缩比例（越大越清晰）
 * @param {(p:number)=>void} [options.onProgress]   进度回调 0~1
 * @param {(stage:string)=>void} [options.onStage]  阶段回调：'mr-prepare' | 'mr-recording'
 * @param {number} [options.maxDimension]           目标最长边（默认按 ratio 计算）
 * @returns {Promise<File|Blob>}
 */
export async function compressVideoWithMediaRecorder(file, options = {}) {
  if (!file) throw new Error('文件为空');
  if (!canRunMediaRecorderCompress()) {
    const err = new Error('当前浏览器不支持 MediaRecorder 视频压缩');
    err.code = 'MEDIARECORDER_UNSUPPORTED';
    throw err;
  }

  const { ratio = 0.6, onProgress, onStage } = options;
  const videoBitsPerSecond = ratioToVideoBitrate(ratio);
  const targetMaxSide = options.maxDimension || ratioToMaxDimension(ratio);
  const { mime, ext } = pickBestRecorderMime();

  try { onStage && onStage('mr-prepare'); } catch (_) { /* ignore */ }

  // 1) 创建隐藏 <video> 用来解码原视频
  const srcUrl = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.src = srcUrl;
  video.muted = true;                 // 必须静音才能自动播放（移动端限制）
  video.playsInline = true;
  video.setAttribute('playsinline', 'true');
  video.setAttribute('webkit-playsinline', 'true');
  video.crossOrigin = 'anonymous';
  video.preload = 'auto';
  // 元素必须在 DOM 里，否则 iOS 有时不解码；用绝对定位挪出可视区
  video.style.cssText = 'position:fixed;left:-99999px;top:-99999px;width:1px;height:1px;opacity:0;pointer-events:none;';
  document.body.appendChild(video);

  // 清理句柄，无论 resolve/reject 都要跑
  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    try { video.pause(); } catch (_) { /* ignore */ }
    try { video.removeAttribute('src'); video.load(); } catch (_) { /* ignore */ }
    try { document.body.removeChild(video); } catch (_) { /* ignore */ }
    try { URL.revokeObjectURL(srcUrl); } catch (_) { /* ignore */ }
  };

  try {
    // 2) 等元数据 & 数据就绪
    await waitCanPlayThrough(video);

    const srcW = video.videoWidth;
    const srcH = video.videoHeight;
    const duration = Number(video.duration) || 0;
    if (!srcW || !srcH) throw new Error('无法读取视频尺寸');
    if (!duration || !isFinite(duration)) {
      // 部分 WebView 的 mov/mp4 metadata 不完整会返回 Infinity；此时无法算进度，但仍可录
      // 用 0 表示未知
    }

    // 3) 计算目标画布尺寸（等比缩放，最长边 = targetMaxSide，且不放大）
    const scale = Math.min(1, targetMaxSide / Math.max(srcW, srcH));
    const dstW = Math.max(2, Math.round(srcW * scale / 2) * 2); // 偶数（H264 要求）
    const dstH = Math.max(2, Math.round(srcH * scale / 2) * 2);

    const canvas = document.createElement('canvas');
    canvas.width = dstW;
    canvas.height = dstH;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('无法创建 2D canvas context');

    // 4) 组装录制 stream：canvas 视频轨 + 原视频音频轨（若可获取）
    // 帧率上限 30fps；实际由绘制循环决定
    const canvasStream = canvas.captureStream(30);

    let audioTrack = null;
    try {
      // captureStream 在 iOS 14+/新版 X5 上可用；老环境返回空 stream 或抛错
      // 有的实现叫 mozCaptureStream，做个兜底
      const capFn = video.captureStream || video.mozCaptureStream;
      if (capFn) {
        const srcStream = capFn.call(video);
        const audioTracks = srcStream.getAudioTracks();
        if (audioTracks && audioTracks[0]) {
          audioTrack = audioTracks[0];
          canvasStream.addTrack(audioTrack);
        }
      }
    } catch (_) {
      // 拿不到音频轨就录成无声视频，不影响主流程
    }

    // 5) MediaRecorder 配置
    const audioBitsPerSecond = 96 * 1000;
    let recorder;
    try {
      recorder = new MediaRecorder(canvasStream, {
        mimeType: mime,
        videoBitsPerSecond,
        audioBitsPerSecond,
      });
    } catch (e) {
      // 极少数环境即使 isTypeSupported 通过也会构造失败，做一次退化
      recorder = new MediaRecorder(canvasStream);
    }

    const chunks = [];
    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) chunks.push(ev.data);
    };

    // 6) 录制完成的 Promise（在 stop 事件里 resolve）
    const stopped = new Promise((resolve, reject) => {
      recorder.onstop = () => resolve();
      recorder.onerror = (ev) => reject(ev.error || new Error('MediaRecorder 内部错误'));
    });

    try { onStage && onStage('mr-recording'); } catch (_) { /* ignore */ }
    recorder.start(1000); // 每秒切一个 chunk，防止部分安卓机 stop 时才吐数据

    // 7) 启动播放 + 绘制循环
    // 尝试提速播放（4x）以缩短压缩时间；不支持的浏览器忽略
    try { video.playbackRate = 4; } catch (_) { /* ignore */ }

    let stopRequested = false;
    const draw = () => {
      if (stopRequested) return;
      try {
        ctx.drawImage(video, 0, 0, dstW, dstH);
      } catch (_) { /* ignore 一帧的绘制失败，避免中断整段 */ }

      // 进度上报（基于 currentTime / duration）
      if (typeof onProgress === 'function' && duration && isFinite(duration)) {
        try {
          onProgress(Math.max(0, Math.min(0.99, video.currentTime / duration)));
        } catch (_) { /* ignore */ }
      }

      // 优先用 requestVideoFrameCallback（专为视频帧同步设计），退化到 rAF
      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(draw);
      } else {
        requestAnimationFrame(draw);
      }
    };

    // 播放起来
    try {
      await video.play();
    } catch (playErr) {
      // 移动端可能因手势策略阻塞；muted 已设，理论上能过。如果仍抛错，说明环境限制严格
      throw new Error(`视频播放失败：${playErr && playErr.message ? playErr.message : playErr}`);
    }
    draw();

    // 8) 等视频播放结束 → 停止 recorder
    await new Promise((resolve) => {
      const onEnded = () => {
        stopRequested = true;
        video.removeEventListener('ended', onEnded);
        resolve();
      };
      video.addEventListener('ended', onEnded, { once: true });
    });

    // 让 recorder 收尾最后一帧数据
    // 有些安卓 X5 在 stop 立即调用时会丢最后 1-2 帧，加个微延时
    await new Promise((r) => setTimeout(r, 200));

    try { recorder.stop(); } catch (_) { /* ignore */ }
    await stopped;

    // 9) 输出封装为 File
    const outBlob = new Blob(chunks, { type: mime.split(';')[0] });
    const outName = pickOutputName(file.name || `video.${ext}`, ext);
    let outFile;
    try {
      outFile = new File([outBlob], outName, { type: outBlob.type });
    } catch (_) {
      outFile = outBlob;
      outFile.name = outName;
    }

    // 完成一次 100%
    try { typeof onProgress === 'function' && onProgress(1); } catch (_) { /* ignore */ }
    return outFile;
  } finally {
    cleanup();
  }
}

export default compressVideoWithMediaRecorder;
